import { motion } from 'framer-motion';
import { Instagram, Linkedin, Twitter, Youtube } from 'lucide-react';
import { toast } from 'sonner';
import appStoreBadge from '../../assets/stores/appstore.png';
import playStoreBadge from '../../assets/stores/playstore.png';
import logoPng from '../../assets/wealthdost-logo.svg';

const footerLinks = {
  company: [
    { name: 'About WealthDost', href: '#' },
    { name: 'Our Mission', href: '#' },
    { name: 'Careers', href: '#' },
    { name: 'Press', href: '#' }
  ],
  product: [
    { name: 'Experts', href: '#experts' },
    { name: 'Tribes', href: '#tribes' },
    { name: 'Features', href: '#features' },
  ],
  community: [
    { name: 'Join Community', href: '#' },
    { name: 'Become an Expert', href: '#' },
    { name: 'Partner With Us', href: '#' },
    { name: 'Blog', href: '#' }
  ],
  legal: [
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms of Service', href: '/terms' },
    { name: 'Disclaimer', href: '/disclaimer' },
    { name: 'Contact', href: '/contact' }
  ]
};

const socialLinks = [
  { icon: Youtube, href: 'https://www.youtube.com/@CAMukulWadhwani', label: 'YouTube' },
  { icon: Twitter, href: 'https://x.com/wealthdostapp', label: 'X' },
  { icon: Linkedin, href: 'https://www.linkedin.com/company/110277227/admin/dashboard/', label: 'LinkedIn' },
  { icon: Instagram, href: 'https://www.instagram.com/wealthdost.official/', label: 'Instagram' }
];

const Footer = () => {
  const APP_STORE_URL = "https://apps.apple.com/us/app/wealthdost/id6758073703";
  const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.fortybears.wealthdost";

  const handleComingSoon = () => {
    toast.info("Coming Soon!", {
      description: "We're working hard to bring this to you. Stay tuned!"
    });
  };

  return (
    <footer id="footer" className="relative pt-20 pb-8" data-testid="footer-section">
      {/* Top Divider */}
      <div className="section-divider mb-20" />

      <div className="max-w-7xl mx-auto px-6">
        {/* CTA Section */}
        <motion.div
          id="download"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] mb-4">
            Join the WealthDost Community
          </h2>
          <p className="text-[#94A3B8] mb-8 max-w-xl mx-auto">
            Be among the first to experience trust-first investing in India
          </p>
          <div className="flex flex-row items-center justify-center gap-4" data-testid="footer-cta">
            <motion.a
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download on the App Store"
            >
              <img src={appStoreBadge} alt="App Store" className="h-12 w-auto" />
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get it on Google Play"
            >
              <img src={playStoreBadge} alt="Google Play" className="h-12 w-auto" />
            </motion.a>
          </div>
        </motion.div>

        {/* Footer Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-16">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1">
            <img 
              src={logoPng} 
              alt="WealthDost" 
              className="h-16 mb-4"
            />
            <p className="text-[#94A3B8] text-sm mb-4">
              Experts You Can Trust
            </p>
            
            {/* Social Links */}
            <div className="flex gap-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-[#1E293B] border border-[#334155] flex items-center justify-center text-[#94A3B8] hover:text-[#F8FAFC] hover:border-[#2563EB] transition-all"
                  aria-label={social.label}
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Links Columns */}
          <div className="hidden">
            <h4 className="text-[#F8FAFC] font-semibold mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); handleComingSoon(); }}
                    className="text-[#94A3B8] hover:text-[#F8FAFC] text-sm transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-[#F8FAFC] font-semibold mb-4">Product</h4>
            <ul className="space-y-3">
              {footerLinks.product.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href}
                    className="text-[#94A3B8] hover:text-[#F8FAFC] text-sm transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="hidden">
            <h4 className="text-[#F8FAFC] font-semibold mb-4">Community</h4>
            <ul className="space-y-3">
              {footerLinks.community.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); handleComingSoon(); }}
                    className="text-[#94A3B8] hover:text-[#F8FAFC] text-sm transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-[#F8FAFC] font-semibold mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href}
                    className="text-[#94A3B8] hover:text-[#F8FAFC] text-sm transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-[#334155]">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[#94A3B8] text-sm">
              © 2026 WealthDost. All rights reserved.
            </p>
            <p className="text-[#94A3B8] text-sm">
              Made with ❤️ for Indian Investors
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
